/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * Angular Build, Upgrade/downgrade stuff
 * @module Angular-Core
 * @preferred
 */

import { Injector } from '@angular/core';
import { IpamProfileCollection } from 'ajs/modules/ipam';
import {
    Auth,
    AviConfirmService,
    AviModalService,
    DefaultValues,
    SchemaService,
    StateManager,
} from 'ajs/modules/core/services';

import {
    HttpWrapper,
    Upload,
} from 'ajs/modules/core/factories';

import { VCenterServerCollection } from 'ajs/modules/cloud';
import {
    WafApplicationSignatureProvider,
    WafApplicationSignatureProviderCollection,
    WafPolicyItem,
    WafPolicyPsmGroupCollection,
    WafPolicyPsmGroupMetric,
    WAF_POLICY_ITEM_TOKEN,
} from 'ajs/modules/waf';

import { VsVip } from 'ajs/modules/vs';

import { LicensingService } from 'ajs/modules/licensing';
import {
    BackupConfiguration,
    BackupConfigurationCollection,
    SystemConfig,
} from 'ajs/modules/system';

import { SEGroup } from 'ajs/modules/service-engine-group';
import { Cloud } from 'ajs/modules/cloud/factories/cloud.item.factory';
import { CLOUD_ITEM_TOKEN } from 'ajs/modules/cloud/cloud.tokens';
import { MacroStackFactory } from 'ajs/modules/data-model/factories/macro-stack.factory';
import { CportalService } from 'ajs/modules/cportal/services';
import { InfraCloudState } from 'ajs/modules/vrf-context/services/infra-cloud-state.service';
import { VRFContextCollection } from
    'ajs/modules/vrf-context/factories/vrf-context.collection.factory';
import {
    INFRA_CLOUD_STATE_SERVICE_TOKEN,
    VRF_CONTEXT_COLLECTION_TOKEN,
} from 'ajs/modules/vrf-context/vrf-context.tokens';
import { SubnetListNetwork, SubnetListNetworkCollection } from 'ajs/modules/network';
import {
    StateParams,
    TransitionService,
} from '@uirouter/core';

import {
    GeoDbCollection,
    GEO_DB_COLLECTION_TOKEN,
} from 'ajs/modules/geo-db/factories/geo-db.collection.factory';

import {
    ErrorPageBodyCollection,
    TErrorPageBodyCollection,
} from 'ajs/modules/error-page/factories/error-page-body.collection.factory';

import {
    IPAM_PROFILE_COLLECTION_TOKEN,
    IPAM_PROFILE_ITEM_TOKEN,
} from 'ajs/modules/ipam/ipam.tokens';

import { IpamProfileItem } from 'ajs/modules/ipam/factories/ipam-profile.item.factory';

import {
    VRFContext,
    VRF_CONTEXT_TOKEN,
} from 'ajs/modules/vrf-context';

import { AutoScalePolicyCollection } from 'ajs/modules/auto-scale';
import {
    CertificateCollection,
    PKIProfileCollection,
    SSLProfileCollection,
} from 'ajs/modules/security';

import {
    PersistenceProfileCollection,
    TPersistenceProfileCollection,
} from 'ajs/modules/persistence-profile/factories/persistence-profile.collection.factory';

import { PASSWORD_GENERATOR_SERVICE_TOKEN } from 'ajs/modules/accounts/accounts.tokens';
import { SECRET_STUB_STRING } from 'ajs/modules/core/core.tokens';

import {
    PasswordGeneratorService,
} from 'ajs/modules/accounts/services/password-generator-service/password-generator.service';

import { ERROR_PAGE_BODY_COLLECTION_TOKEN } from 'ajs/modules/error-page/error-page.tokens';
import { Collection } from 'ajs/modules/data-model/factories/collection.factory';

import {
    HealthMonitorCollection,
    THealthMonitorCollection,
} from 'ajs/modules/healthmonitor/factories/health-monitor.collection.factory';

import {
    IPReputationDB,
} from 'ajs/modules/ip-reputation-db/factories/ip-reputation-db.item.factory';

import {
    ApplicationInsightsPolicyCollection,
} from 'ajs/modules/application-insights-policy/factories/application-insights-policy.collection.factory';
import {
    APPLICATION_INSIGHTS_POLICY_COLLECTION_TOKEN,
} from 'ajs/modules/application-insights-policy/application-insights-policy.tokens';

import { PositiveSecurityPolicyCollection } from 'ajs/modules/positive-security-policy/factories';
import {
    POSITIVE_SECURITY_POLICY_COLLECTION_TOKEN,
} from 'ajs/modules/positive-security-policy/positive-security-policy.tokens';

const $injector = '$injector';

/**
 * Used to upgrade providers from AngularJS to be used in Angular.
 * @param serviceName Service to be injected from AngularJS.
 */
export function matchTargetConfigItemFactory(injector: Injector): any {
    return injector.get('MatchTargetConfigItem');
}

export const matchTargetConfigItemProvider = {
    deps: [$injector],
    provide: 'MatchTargetConfigItem',
    useFactory: matchTargetConfigItemFactory,
};

export const schemaServiceProvider = {
    deps: [$injector],
    provide: SchemaService,
    useFactory(injector: Injector): SchemaService {
        return injector.get('schemaService');
    },
};

export const aviModalProvider = {
    deps: [$injector],
    provide: AviModalService,
    useFactory(injector: Injector): AviModalService {
        return injector.get('AviModal');
    },
};

export const aviConfirmServiceProvider = {
    deps: [$injector],
    provide: AviConfirmService,
    useFactory(injector: Injector): AviConfirmService {
        return injector.get('AviConfirmService');
    },
};

export const wafPolicyPsmGroupCollectionProvider = {
    deps: [$injector],
    provide: WafPolicyPsmGroupCollection,
    useFactory(injector: Injector): typeof WafPolicyPsmGroupCollection {
        return injector.get('WafPolicyPsmGroupCollection');
    },
};

export const wafApplicationSignatureProviderCollectionProvider = {
    deps: [$injector],
    provide: WafApplicationSignatureProviderCollection,
    useFactory(injector: Injector): typeof WafApplicationSignatureProviderCollection {
        return injector.get('WafApplicationSignatureProviderCollection');
    },
};

export const wafApplicationSignatureProviderProvider = {
    deps: [$injector],
    provide: WafApplicationSignatureProvider,
    useFactory(injector: Injector): typeof WafApplicationSignatureProvider {
        return injector.get('WafApplicationSignatureProvider');
    },
};

export const alertMetricsCollectionProvider = {
    deps: [$injector],
    provide: 'alertMetricsCollection',
    useFactory(injector: Injector): any {
        return injector.get('AlertMetricsCollection');
    },
};

export const wafPolicyPsmGroupMetricProvider = {
    deps: [$injector],
    provide: WafPolicyPsmGroupMetric,
    useFactory(injector: Injector): typeof WafPolicyPsmGroupMetric {
        return injector.get('WafPolicyPsmGroupMetric');
    },
};

export function timeframeFactory(injector: Injector): any {
    return injector.get('Timeframe');
}

export const timeframeProvider = {
    deps: [$injector],
    provide: 'Timeframe',
    useFactory: timeframeFactory,
};

export function appStateTreeFactory(injector: Injector): any {
    return injector.get('appStateTree');
}

export const appStateTreeProvider = {
    deps: [$injector],
    provide: 'appStateTree',
    useFactory: appStateTreeFactory,
};

export function $stateFactory(injector: Injector): any {
    return injector.get('$state');
}

export const $stateProvider = {
    deps: [$injector],
    provide: '$state',
    useFactory: $stateFactory,
};

export const $stateParamsProvider = {
    deps: [$injector],
    provide: '$stateParams',
    useFactory(injector: Injector): StateParams {
        return injector.get('$stateParams');
    },
};

export const $transitionsProvider = {
    deps: [$injector],
    provide: '$transitions',
    useFactory(injector: Injector): TransitionService {
        return injector.get('$transitions');
    },
};

export function upgradeNodeCollectionFactory(injector: Injector): any {
    return injector.get('UpgradeNodeCollection');
}

export const UpgradeNodeCollectionProvider = {
    deps: [$injector],
    provide: 'UpgradeNodeCollection',
    useFactory: upgradeNodeCollectionFactory,
};

export function licensingServiceFactory(injector: Injector): LicensingService {
    return injector.get('licensingService');
}

export const licensingServiceProvider = {
    deps: [$injector],
    provide: LicensingService,
    useFactory: licensingServiceFactory,
};

export const cloudConnectorUserCollectionProvider = {
    deps: [$injector],
    provide: 'CloudConnectorUserCollection',
    useFactory(injector: Injector): any {
        return injector.get('CloudConnectorUserCollection');
    },
};

export const cloudCollectionProvider = {
    deps: [$injector],
    provide: 'CloudCollection',
    useFactory(injector: Injector): any {
        return injector.get('CloudCollection');
    },
};

export const ipamProfileCollectionProvider = {
    deps: [$injector],
    provide: IpamProfileCollection,
    useFactory(injector: Injector): typeof IpamProfileCollection {
        return injector.get(IPAM_PROFILE_COLLECTION_TOKEN);
    },
};

export const vCenterServerCollectionProvider = {
    deps: [$injector],
    provide: VCenterServerCollection,
    useFactory(injector: Injector): typeof VCenterServerCollection {
        return injector.get('VCenterServerCollection');
    },
};

export const authServiceProvider = {
    deps: [$injector],
    provide: Auth,
    useFactory(injector: Injector): Auth {
        return injector.get('Auth');
    },
};

export const stateManagerServiceProvider = {
    deps: [$injector],
    provide: StateManager,
    useFactory($injector: Injector): StateManager {
        return $injector.get('stateManagerService');
    },
};

export const uploadProvider = {
    deps: [$injector],
    provide: Upload,
    useFactory(i: Injector): typeof Upload {
        return i.get('Upload');
    },
};

export const macroStackProvider = {
    deps: [$injector],
    provide: MacroStackFactory,
    useFactory(injector: Injector): typeof MacroStackFactory {
        return injector.get('MacroStackFactory');
    },
};

export const systemConfigProvider = {
    deps: [$injector],
    provide: SystemConfig,
    useFactory(injector: Injector): typeof SystemConfig {
        return injector.get('SystemConfig');
    },
};

export const backupConfigItemProvider = {
    deps: [$injector],
    provide: BackupConfiguration,
    useFactory(injector: Injector): typeof BackupConfiguration {
        return injector.get('BackupConfiguration');
    },
};

export const backupConfigCollectionProvider = {
    deps: [$injector],
    provide: BackupConfigurationCollection,
    useFactory(injector: Injector): typeof BackupConfigurationCollection {
        return injector.get('BackupConfigurationCollection');
    },
};

export const defaultValuesProvider = {
    deps: [$injector],
    provide: DefaultValues,
    useFactory(injector: Injector): typeof DefaultValues {
        return injector.get('defaultValues');
    },
};

export const cloudItemProvider = {
    deps: [$injector],
    provide: Cloud,
    useFactory(injector: Injector): typeof Cloud {
        return injector.get(CLOUD_ITEM_TOKEN);
    },
};

export const cportalServiceProvider = {
    deps: [$injector],
    provide: CportalService,
    useFactory(injector: Injector): CportalService {
        return injector.get('CportalService');
    },
};

export const infraCloudStateServiceProvider = {
    deps: [$injector],
    provide: InfraCloudState,
    useFactory(injector: Injector): InfraCloudState {
        return injector.get(INFRA_CLOUD_STATE_SERVICE_TOKEN);
    },
};

export const subnetlistNetworkCollectionProvider = {
    deps: [$injector],
    provide: SubnetListNetworkCollection,
    useFactory(injector: Injector): typeof SubnetListNetworkCollection {
        return injector.get('SubnetListNetworkCollection');
    },
};

export const HttpWrapperProvider = {
    deps: [$injector],
    provide: HttpWrapper,
    useFactory(injector: Injector): typeof HttpWrapper {
        return injector.get('HttpWrapper');
    },
};

export const vrfContextCollectionProvider = {
    deps: [$injector],
    provide: VRFContextCollection,
    useFactory(injector: Injector): typeof VRFContextCollection {
        return injector.get(VRF_CONTEXT_COLLECTION_TOKEN);
    },
};

export const geoDbCollectionProvider = {
    deps: [$injector],
    provide: GeoDbCollection,
    useFactory(injector: Injector): typeof GeoDbCollection {
        return injector.get(GEO_DB_COLLECTION_TOKEN);
    },
};

export const healthMonitorCollectionProvider = {
    deps: [$injector],
    provide: HealthMonitorCollection,
    useFactory(injector: Injector): THealthMonitorCollection {
        return injector.get('HealthMonitorCollection');
    },
};

export const autoScaleLaunchConfigCollectionProvider = {
    deps: [$injector],
    provide: 'AutoScaleLaunchConfigCollection',
    useFactory(injector: Injector): any {
        return injector.get('AutoScaleLaunchConfigCollection');
    },
};

export const analyticsProfileCollectionProvider = {
    deps: [$injector],
    provide: 'AnalyticsProfileCollection',
    useFactory(injector: Injector): any {
        return injector.get('AnalyticsProfileCollection');
    },
};

export const applicationInsightsPolicyCollectionProvider = {
    deps: [$injector],
    provide: ApplicationInsightsPolicyCollection,
    useFactory(injector: Injector): any {
        return injector.get(APPLICATION_INSIGHTS_POLICY_COLLECTION_TOKEN);
    },
};

export const positiveSecurityPolicyCollectionProvider = {
    deps: [$injector],
    provide: PositiveSecurityPolicyCollection,
    useFactory(injector: Injector): any {
        return injector.get(POSITIVE_SECURITY_POLICY_COLLECTION_TOKEN);
    },
};

export const autoScalePolicyCollectionProvider = {
    deps: [$injector],
    provide: AutoScalePolicyCollection,
    useFactory(injector: Injector): any {
        return injector.get('AutoScalePolicyCollection');
    },
};

export const persistenceProfileCollectionProvider = {
    deps: [$injector],
    provide: PersistenceProfileCollection,
    useFactory(injector: Injector): TPersistenceProfileCollection {
        return injector.get('PersistenceProfileCollection');
    },
};

export const clusterNodeCollectionProvider = {
    deps: [$injector],
    provide: 'ClusterNodeCollection',
    useFactory(injector: Injector): any {
        return injector.get('ClusterNodeCollection');
    },
};

export const errorPageBodyCollectionProvider = {
    deps: [$injector],
    provide: ErrorPageBodyCollection,
    useFactory(injector: Injector): TErrorPageBodyCollection {
        return injector.get(ERROR_PAGE_BODY_COLLECTION_TOKEN);
    },
};

export const subnetListNetworkItemProvider = {
    deps: [$injector],
    provide: SubnetListNetwork,
    useFactory(injector: Injector): SubnetListNetwork {
        return injector.get('SubnetListNetwork');
    },
};

export const ipamProfileItemProvider = {
    deps: [$injector],
    provide: IpamProfileItem,
    useFactory(injector: Injector): typeof IpamProfileItem {
        return injector.get(IPAM_PROFILE_ITEM_TOKEN);
    },
};

export const vrfContextItemProvider = {
    deps: [$injector],
    provide: VRFContext,
    useFactory(injector: Injector): typeof VRFContext {
        return injector.get(VRF_CONTEXT_TOKEN);
    },
};

export const sslProfileCollectionProvider = {
    deps: [$injector],
    provide: SSLProfileCollection,
    useFactory(injector: Injector): typeof SSLProfileCollection {
        return injector.get('SSLProfileCollection');
    },
};

export const pkiProfileCollectionProvider = {
    deps: [$injector],
    provide: PKIProfileCollection,
    useFactory(injector: Injector): typeof PKIProfileCollection {
        return injector.get('PKIProfileCollection');
    },
};

export const certificateCollectionProvider = {
    deps: [$injector],
    provide: CertificateCollection,
    useFactory(injector: Injector): typeof CertificateCollection {
        return injector.get('CertificateCollection');
    },
};

export const dnsProvider = {
    deps: [$injector],
    provide: 'DNS',
    useFactory(injector: Injector): any {
        return injector.get('DNS');
    },
};

export const VMNicIPCollectionProvider = {
    deps: [$injector],
    provide: 'VMNicIPCollection',
    useFactory(injector: Injector): any {
        return injector.get('VMNicIPCollection');
    },
};

export const passwordGeneratorServiceProvider = {
    deps: [$injector],
    provide: PasswordGeneratorService,
    useFactory(injector: Injector): typeof PasswordGeneratorService {
        return injector.get(PASSWORD_GENERATOR_SERVICE_TOKEN);
    },
};

export const secretStubStrProvider = {
    deps: [$injector],
    provide: 'secretStubStr',
    useFactory(injector: Injector): string {
        return injector.get(SECRET_STUB_STRING);
    },
};

export const seGroupFlavorCollectionProvider = {
    deps: [$injector],
    provide: 'SEGroupFlavorCollection',
    useFactory(injector: Injector): any {
        return injector.get('SEGroupFlavorCollection');
    },
};

export const baseCollectionProvider = {
    deps: [$injector],
    provide: 'Collection',
    useFactory(injector: Injector): typeof Collection {
        return injector.get('Collection');
    },
};

export const systemConfigServiceProvider = {
    deps: [$injector],
    provide: 'systemConfigService',
    useFactory(injector: Injector): SystemConfig {
        return injector.get('systemConfigService');
    },
};

export const wafPolicyProvider = {
    deps: [$injector],
    provide: WafPolicyItem,
    useFactory(injector: Injector): typeof WafPolicyItem {
        return injector.get(WAF_POLICY_ITEM_TOKEN);
    },
};

export const vipProvider = {
    deps: [$injector],
    provide: VsVip,
    useFactory(injector: Injector): typeof VsVip {
        return injector.get('VsVip');
    },
};

export const logRecordCollectionProvider = {
    deps: [$injector],
    provide: 'LogRecordCollection',
    useFactory(injector: Injector): any {
        return injector.get('LogRecordCollection');
    },
};

export const inventoryMapCollectionProvider = {
    deps: [$injector],
    provide: 'InventoryMapCollection',
    useFactory(injector: Injector): any {
        return injector.get('InventoryMapCollection');
    },
};

export const eventCollectionProvider = {
    deps: [$injector],
    provide: 'EventCollection',
    useFactory(injector: Injector): any {
        return injector.get('EventCollection');
    },
};

export const anomalyCollectionProvider = {
    deps: [$injector],
    provide: 'AnomalyCollection',
    useFactory(injector: Injector): any {
        return injector.get('AnomalyCollection');
    },
};

export const seGroupItemProvider = {
    deps: [$injector],
    provide: SEGroup,
    useFactory(injector: Injector): typeof SEGroup {
        return injector.get('SEGroup');
    },
};

export const ipReputationDBItemProvider = {
    deps: [$injector],
    provide: IPReputationDB,
    useFactory(injector: Injector): typeof IPReputationDB {
        return injector.get('IPReputationDB');
    },
};

export const namesHelperProvider = {
    deps: [$injector],
    provide: 'NamesHelper',
    useFactory(injector: Injector): any {
        return injector.get('NamesHelper');
    },
};

export const virtualServiceProvider = {
    deps: [$injector],
    provide: 'VirtualService',
    useFactory(injector: Injector): any {
        return injector.get('VirtualService');
    },
};
